import { Tooltip as AntdTooltip, Flex } from 'antd';
import { isValidElement, useEffect, useRef, useState } from 'react';
import clsx from 'clsx';
import toast from 'react-hot-toast';
import { CopyIcon, TickIcon } from 'common/all-icons/custom-icons';
import styles from './CopyToClipboard.module.scss';

const extractText = (node) => {
  if (node === null || node === undefined || typeof node === 'boolean') return '';
  if (typeof node === 'string' || typeof node === 'number') return String(node);
  if (Array.isArray(node)) return node.map(extractText).filter(Boolean).join(', ');
  if (isValidElement(node)) return extractText(node.props?.children);
  return '';
};

const CopyToClipboard = ({
  value = '',
  copyValue,
  textClassName = 'body-12-medium',
  maxWidth = 'clamp(170px, 24vw, 300px)',
  iconSize = 16,
  allowCopy = true,
  hideText = false,
  isAnimate = true,
  isLightBg = false,
  isHtmlBody = false,
}) => {
  const textRef = useRef(null);
  const copiedTimer = useRef(null);
  const blinkTimer = useRef(null);
  const [isTruncated, setIsTruncated] = useState(false);
  const [isCopied, setIsCopied] = useState(false);
  const [isBlinking, setIsBlinking] = useState(false);

  useEffect(() => {
    const el = textRef.current;
    if (!el) return undefined;

    const checkTruncation = () => setIsTruncated(el.scrollWidth > el.clientWidth + 1);
    checkTruncation();

    const observer = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(checkTruncation) : null;
    observer?.observe(el);
    window.addEventListener('resize', checkTruncation);

    return () => {
      observer?.disconnect();
      window.removeEventListener('resize', checkTruncation);
    };
  }, [value]);

  useEffect(
    () => () => {
      clearTimeout(copiedTimer.current);
      clearTimeout(blinkTimer.current);
    },
    [],
  );

  if (!value) return '-';

  const textToCopy = copyValue ?? (typeof value === 'string' || typeof value === 'number' ? value : extractText(value));

  const handleCopy = async (e) => {
    if (isCopied) return;
    e?.stopPropagation?.();
    e?.preventDefault?.();
    if (!textToCopy) {
      toast.error('Nothing to copy!');
      return;
    }
    try {
      await navigator.clipboard.writeText(textToCopy);
      setIsCopied(true);
      setIsBlinking(true);
      clearTimeout(copiedTimer.current);
      clearTimeout(blinkTimer.current);
      blinkTimer.current = setTimeout(() => setIsBlinking(false), 2000);
      copiedTimer.current = setTimeout(() => setIsCopied(false), 2000);
    } catch {
      toast.error('Unable to copy!');
    }
  };

  return (
    <Flex
      className={clsx(styles.card, isLightBg && styles.lightBg, isAnimate && isBlinking && styles.blink)}
      align="center"
      justify="space-between"
      style={{ maxWidth }}
    >
      <AntdTooltip
        title={typeof value === 'string' || typeof value === 'number' ? value : textToCopy}
        open={isTruncated ? undefined : false}
        trigger={['hover', 'click']}
        placement="top"
        color="var(--user-dropdown-bg)"
        overlayClassName={styles.copyTooltip}
      >
        {!hideText && (
          <span
            ref={textRef}
            className={clsx(
              textClassName,
              styles.truncate,
              isHtmlBody && styles.htmlBody,
              isHtmlBody && isBlinking && styles.selectedTextField,
            )}
          >
            {value}
          </span>
        )}
      </AntdTooltip>
      {allowCopy && (
        <span
          role="button"
          tabIndex={0}
          aria-label={isCopied ? 'Copied' : 'Copy'}
          className={clsx(styles.copyButton, isCopied && styles.copied)}
          onClick={handleCopy}
          onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && handleCopy(e)}
        >
          <span className={clsx(styles.icon, styles.copyIcon)}>
            <CopyIcon style={{ flexShrink: 0 }} width={iconSize} height={iconSize} stroke="var(--ant-color-primary)" />
          </span>

          <span className={clsx(styles.icon, styles.tickIcon)}>
            <TickIcon
              style={{ flexShrink: 0 }}
              width={iconSize}
              height={iconSize}
              stroke="var(--user-main-green)"
              strokeWidth={1.2}
            />
          </span>
        </span>
      )}
    </Flex>
  );
};

export default CopyToClipboard;
